import { DotLegalDatePicker, DotLegalFullScreenSpinner, DotLegalHeader, DotLegalRadioButton, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { Box, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { BodyTypography, HeaderBodyTypography, HeaderBox, TextBoxWrapper } from "../ProcessingActivityReport";
import React from "react";
import { useResearchEthicsApprovalReportHooks } from "./ResearchEthicsApprovalReport.hooks";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import ResearchEthicsApprovalReportDocuments from "./researchEthicsApprovalReportDocuments/ResearchEthicsApprovalReportDocuments";
import ProcessingActivityReportDialogHeader from "../processingActivityReportDialogHeader/ProcessingActivityReportDialogHeader";
import { CustomerReportType } from "../../customer/addEditCustomerDialog/AddEditCustomer.types";
import SpanBox from "../SpanBox.tsx";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";

export interface IResearchEthicsApprovalReport {
    processingActivityId?: string;
    isPDF?: boolean;
}

function ResearchEthicsApprovalReport(props: IResearchEthicsApprovalReport) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const { data, isLoading, reportUpdater, refetch, processingActivityId } = useResearchEthicsApprovalReportHooks(props);
    const { customerName } = useUserContext();

    function getApplicantContent() {
        return (
            <Box className="avoid-page-break">
                {getContentHeader("applicant", true)}
                <HeaderBox>
                    <HeaderBodyTypography>{translateString("projectManager") + ": "}</HeaderBodyTypography>
                    <BodyTypography>{data?.projectManager}</BodyTypography>
                </HeaderBox>

                <HeaderBox>
                    <HeaderBodyTypography>{translateString("department") + ": "}</HeaderBodyTypography>
                    <BodyTypography>{data?.businessAreas.join(", ")}</BodyTypography>
                </HeaderBox>

                <HeaderBox>
                    <HeaderBodyTypography>{translateString("emailAddress") + ": "}</HeaderBodyTypography>
                    <BodyTypography>{data?.email}</BodyTypography>
                </HeaderBox>

                <HeaderBox>
                    <HeaderBodyTypography>{translateString("phoneNumber") + ": "}</HeaderBodyTypography>
                    <Box marginLeft={1}>
                        <DotLegalTextField
                            size="small"
                            noMargin
                            label={""}
                            value={data?.phone ?? ""}
                            debounce
                            onChange={(value) => reportUpdater.onPhoneChange(value)}
                        />
                    </Box>
                </HeaderBox>

                <HeaderBox>
                    <HeaderBodyTypography>{translateString("otherProjectParticipants") + ": "}</HeaderBodyTypography>
                    <BodyTypography>{data?.otherProjectParticipants.join(", ")}</BodyTypography>
                </HeaderBox>
            </Box>
        );
    }

    function getProjectInformationContent() {
        return (
            <Box className="avoid-page-break">
                {getContentHeader("projectInformation")}
                <HeaderBox>
                    <HeaderBodyTypography>{translateString("projectName") + ": "}</HeaderBodyTypography>
                    <BodyTypography>{data?.projectName}</BodyTypography>
                </HeaderBox>
                <HeaderBox>
                    <BodyTypography>
                        <HeaderBodyTypography>{translateString("description") + ": "}</HeaderBodyTypography>
                        <SpanBox>
                            <MentionInputRender value={data?.description ?? ""} />
                        </SpanBox>
                    </BodyTypography>
                </HeaderBox>
                <HeaderBox>
                    <Box sx={{ width: 145 }}>
                        <HeaderBodyTypography>{translateString("expectedStartDate") + ": "}</HeaderBodyTypography>
                    </Box>
                    <Box>
                        <DotLegalDatePicker
                            label=""
                            value={data?.expectedStartDate ?? null}
                            onChange={reportUpdater.onExpectedStartDateChange}
                            language={getDateTimePickerLanguage()}
                            noMargin
                        />
                    </Box>
                </HeaderBox>
                <HeaderBox>
                    <Box sx={{ width: 145 }}>
                        <HeaderBodyTypography>{translateString("expectedEndDate") + ": "}</HeaderBodyTypography>
                    </Box>
                    <Box>
                        <DotLegalDatePicker
                            label=""
                            value={data?.expectedEndDate ?? null}
                            onChange={reportUpdater.onExpectedEndDateChange}
                            language={getDateTimePickerLanguage()}
                            noMargin
                        />
                    </Box>
                </HeaderBox>
                <div className="page-break-inside">
                    {getTextFieldWrapper("projectFinancierHeader", data?.projectFinancier, reportUpdater.onProjectFinancierChange)}
                </div>
                {getTextFieldWrapper("ethicalApprovalRequiredByHeader", data?.ethicalApprovalRequiredBy, reportUpdater.onEthicalApprovalRequiredByChange)}
            </Box>
        );
    }

    function getCheckListContent() {
        return (
            <React.Fragment>
                <div className="avoid-page-break">
                    {getContentHeader("checkList")}
                    <DotLegalHeader headerStyle="small" fontWeight={400}>
                        <Box sx={{ whiteSpace: "pre-line" }}>{translateString("checkListInfo")}</Box>
                    </DotLegalHeader>
                    {checkListRadioButtons()}
                </div>
            </React.Fragment>
        );
    }

    function getProjectDescriptionContent() {
        return (
            <Box className="avoid-page-break">
                <div className="avoid-page-break">
                    {getContentHeader("projectDescription")}

                    <DotLegalHeader headerStyle="small" fontWeight={400}>
                        <Box sx={{ whiteSpace: "pre-line" }}>{translateString("projectDescriptionInfo")}</Box>
                    </DotLegalHeader>
                </div>
                {!props.isPDF && (
                    <React.Fragment>
                        <DotLegalHeader headerStyle="small">{translateString("uploadAttachments")}</DotLegalHeader>

                        <Box marginBottom={5}>
                            <ResearchEthicsApprovalReportDocuments
                                refetch={() => refetch()}
                                processingActivityId={processingActivityId}
                                documents={data?.documents ?? []}
                                onRemoveDocument={reportUpdater.onRemoveDocument}
                            />
                        </Box>
                    </React.Fragment>
                )}

                {getTextFieldWrapper("participantsHeader", data?.participants, reportUpdater.onParticipantsChange)}
                {getTextFieldWrapper(
                    "participantsRecruitmentMethodHeader",
                    data?.participantsRecruitmentMethod,
                    reportUpdater.onParticipantsRecruitmentMethodChange
                )}

                {getTextFieldWrapper("participantEngagementHeader", data?.participantEngagement, reportUpdater.onParticipantEngagementChange)}

                <div className="avoid-page-break">
                    {getTextFieldWrapper(
                        "informedConsentObtainingMethodHeader",
                        data?.informedConsentObtainingMethod,
                        reportUpdater.onInformedConsentObtainingMethodChange
                    )}
                </div>
                {getTextFieldWrapper("dataGenerationAndStorageHeader", data?.dataGenerationAndStorage, reportUpdater.onDataGenerationAndStorageChange)}
                {getTextFieldWrapper(
                    "inclusionOrExclusionCriteriaHeader",
                    data?.inclusionOrExclusionCriteria,
                    reportUpdater.onInclusionOrExclusionCriteriaChange
                )}
                {getTextFieldWrapper("usedRegistryDataHeader", data?.usedRegistryData, reportUpdater.onUsedRegistryDataChange)}
            </Box>
        );
    }

    function getParticipantsAndConfidentiallyContent() {
        return (
            <React.Fragment>
                <div className="avoid-page-break">
                    {getContentHeader("participantsAndConfidentially")}

                    <DotLegalHeader headerStyle="small" fontWeight={400}>
                        {translateString("participantsAndConfidentiallyInfo", { name: customerName })}
                    </DotLegalHeader>

                    <Box marginBottom={3}>{getParticipantsAndConfidentiallyRadioButtons()}</Box>

                    {getTextFieldWrapper(
                        "otherInformationRelatedToPersonalDataHeader",
                        data?.otherInformationRelatedToPersonalData,
                        reportUpdater.onOtherInformationRelatedToPersonalDataChange
                    )}
                </div>
            </React.Fragment>
        );
    }

    function getFinancialIncentivesContent() {
        return (
            <Box className="avoid-page-break">
                <div className="avoid-page-break">
                    {getContentHeader("financialIncentives")}

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <HeaderBodyTypography>{translateString("isCompensationProvidedToResearchSubjectsHeader") + ": "}</HeaderBodyTypography>

                        <RadioGroup
                            row
                            value={data?.isCompensationProvidedToResearchSubjects}
                            onChange={(e) => {
                                const value = (e.target as HTMLInputElement).value === "true";
                                reportUpdater.onIsCompensationProvidedToResearchSubjectsChange(value);
                            }}
                        >
                            <Box marginLeft={2}>
                                <DotLegalRadioButton value label={translateString("yes")}></DotLegalRadioButton>
                                <DotLegalRadioButton value={false} label={translateString("no")}></DotLegalRadioButton>
                            </Box>
                        </RadioGroup>
                    </Box>
                </div>
                {props.isPDF ? (
                    <BodyTypography sx={{ marginBottom: 2 }}>
                        <SpanBox>{data?.amountAndFormOfCompensation ?? ""}</SpanBox>
                    </BodyTypography>
                ) : (
                    <TextBoxWrapper>
                        <DotLegalTextField
                            onChange={reportUpdater.onAmountAndFormOfCompensationChange}
                            label={""}
                            placeholder={""}
                            value={data?.amountAndFormOfCompensation ?? ""}
                            debounce
                            multiline
                            rows={8}
                        />
                    </TextBoxWrapper>
                )}
            </Box>
        );
    }

    function getEnvironmentAndSafetyContent() {
        return (
            <Box className="avoid-page-break">
                {getContentHeader("environmentAndSafety")}

                {getTextFieldWrapper("harmMinimizationMeasuresHeader", data?.harmMinimizationMeasures, reportUpdater.onHarmMinimizationMeasuresChange)}
            </Box>
        );
    }

    function getOtherConditionsContent() {
        return (
            <Box className="avoid-page-break">
                {getContentHeader("otherConditions")}

                {getTextFieldWrapper("additionalEthicalFactorsHeader", data?.additionalEthicalFactors, reportUpdater.onAdditionalEthicalFactorsChange)}
                <div className="avoid-page-break">
                    {getTextFieldWrapper("ethicalPointsOfAttentionHeader", data?.ethicalPointsOfAttention, reportUpdater.onEthicalPointsOfAttentionChange)}
                </div>
            </Box>
        );
    }

    function getSignatureContent() {
        return (
            <Box className="avoid-page-break">
                {getContentHeader("signature")}

                {getTextFieldWrapper("signatureHeader", data?.signature, reportUpdater.onSignatureChange)}
            </Box>
        );
    }

    function getTextFieldWrapper(header: string, value: string | undefined, onChange: (value: string) => void) {
        return (
            <Box className="avoid-page-break">
                <HeaderBodyTypography>{translateString(header)}</HeaderBodyTypography>
                {props.isPDF ? (
                    <>
                        <BodyTypography sx={{ marginBottom: 2 }}>
                            <SpanBox>{value ?? ""}</SpanBox>
                        </BodyTypography>
                    </>
                ) : (
                    <TextBoxWrapper>
                        <DotLegalTextField onChange={(value) => onChange(value)} label={""} placeholder={""} value={value ?? ""} debounce multiline rows={8} />
                    </TextBoxWrapper>
                )}
            </Box>
        );
    }

    function checkListRadioButtons() {
        return (
            <Grid container>
                <Grid size={9}></Grid>
                <Grid display={"flex"} justifyContent={"center"} size={1}>
                    <DotLegalHeader headerStyle="extraSmall">{translateString("yesOrMaybe")}</DotLegalHeader>
                </Grid>
                <Grid display={"flex"} justifyContent={"center"} size={1}>
                    <DotLegalHeader headerStyle="extraSmall">{translateString("no")}</DotLegalHeader>
                </Grid>
                <Grid display={"flex"} justifyContent={"center"} size={1}>
                    <DotLegalHeader headerStyle="extraSmall">{translateString("notApplicable")}</DotLegalHeader>
                </Grid>
                {getCheckListRadioButton(
                    "areUninformedParticipantsPresent",
                    data?.areUninformedParticipantsPresent,
                    reportUpdater.onAreUninformedParticipantsPresentChange
                )}
                {getCheckListRadioButton(
                    "involvesCheatingOrDeceptionOfParticipants",
                    data?.involvesCheatingOrDeceptionOfParticipants,
                    reportUpdater.onInvolvesCheatingOrDeceptionOfParticipantsChange
                )}
                {getCheckListRadioButton("includesSensitiveTopics", data?.includesSensitiveTopics, reportUpdater.onIncludesSensitiveTopicsChange)}
                {getCheckListRadioButton(
                    "canCauseStressOrDiscomfortForParticipants",
                    data?.canCauseStressOrDiscomfortForParticipants,
                    reportUpdater.onCanCauseStressOrDiscomfortForParticipantsChange
                )}
                {getCheckListRadioButton(
                    "requiresFrequentTestingOfParticipants",
                    data?.requiresFrequentTestingOfParticipants,
                    reportUpdater.onRequiresFrequentTestingOfParticipantsChange
                )}
                {getCheckListRadioButton(
                    "posesSecurityRiskToResearchers",
                    data?.posesSecurityRiskToResearchers,
                    reportUpdater.onPosesSecurityRiskToResearchersChange
                )}
                {getCheckListRadioButton(
                    "involvesVulnerableParticipants",
                    data?.involvesVulnerableParticipants,
                    reportUpdater.onInvolvesVulnerableParticipantsChange
                )}
                {getCheckListRadioButton(
                    "involvesParticipantsUnder15OrOver15ButUnableToProvideConsent",
                    data?.involvesParticipantsUnder15OrOver15ButUnableToProvideConsent,
                    reportUpdater.onInvolvesParticipantsUnder15OrOver15ButUnableToProvideConsentChange
                )}
                {getCheckListRadioButton(
                    "investigatesIllegalBehaviorOrCriminalActions",
                    data?.investigatesIllegalBehaviorOrCriminalActions,
                    reportUpdater.onInvestigatesIllegalBehaviorOrCriminalActionsChange
                )}
                {getCheckListRadioButton(
                    "isInternationalAndRaisesSpecificEthicalQuestions",
                    data?.isInternationalAndRaisesSpecificEthicalQuestions,
                    reportUpdater.onIsInternationalAndRaisesSpecificEthicalQuestionsChange
                )}
                {getCheckListRadioButton("hasHighRiskOfResultsMisuse", data?.hasHighRiskOfResultsMisuse, reportUpdater.onHasHighRiskOfResultsMisuseChange)}
                {getCheckListRadioButton(
                    "hasDoubtAboutAIUsageTransparency",
                    data?.hasDoubtAboutAIUsageTransparency,
                    reportUpdater.onHasDoubtAboutAIUsageTransparencyChange
                )}
            </Grid>
        );
    }

    function getParticipantsAndConfidentiallyRadioButtons() {
        return (
            <Grid container>
                <Grid size={9}></Grid>
                <Grid display={"flex"} justifyContent={"center"} size={1}>
                    <DotLegalHeader headerStyle="extraSmall">{translateString("yes")}</DotLegalHeader>
                </Grid>
                <Grid display={"flex"} justifyContent={"center"} size={1}>
                    <DotLegalHeader headerStyle="extraSmall">{translateString("no")}</DotLegalHeader>
                </Grid>
                <Grid display={"flex"} justifyContent={"center"} size={1}>
                    <DotLegalHeader headerStyle="extraSmall">{translateString("dontKnow")}</DotLegalHeader>
                </Grid>
                {getCheckListRadioButton(
                    "isLegalBasisEstablishedBeforeProjectCommences",
                    data?.isLegalBasisEstablishedBeforeProjectCommences,
                    reportUpdater.onIsLegalBasisEstablishedBeforeProjectCommencesChange
                )}
                {getCheckListRadioButton(
                    "isDataCollectedAndProcessedSecurely",
                    data?.isDataCollectedAndProcessedSecurely,
                    reportUpdater.onIsDataCollectedAndProcessedSecurelyChange
                )}
                {getCheckListRadioButton("isDataHandedOverToGDPRUnit", data?.isDataHandedOverToGDPRUnit, reportUpdater.onIsDataHandedOverToGDPRUnitChange)}
                {getCheckListRadioButton(
                    "isDataTransferredToUnsafeCountries",
                    data?.isDataTransferredToUnsafeCountries,
                    reportUpdater.onIsDataTransferredToUnsafeCountriesChange
                )}
                {getCheckListRadioButton(
                    "isDataUsedOrDisclosedWithoutConsent",
                    data?.isDataUsedOrDisclosedWithoutConsent,
                    reportUpdater.onIsDataUsedOrDisclosedWithoutConsentChange
                )}
                {getCheckListRadioButton(
                    "areEmployeesInformedAboutDataHandling",
                    data?.areEmployeesInformedAboutDataHandling,
                    reportUpdater.onAreEmployeesInformedAboutDataHandlingChange
                )}
            </Grid>
        );
    }

    function getCheckListRadioButton(label: string, value: number | undefined, onChange: (value: number) => void) {
        return (
            <React.Fragment>
                <Grid size={9}>
                    <DotLegalHeader fontWeight={400} headerStyle="small">
                        {translateString(label)}
                    </DotLegalHeader>
                </Grid>
                <Grid size={3}>
                    <RadioGroup
                        row
                        value={value}
                        onChange={(e) => {
                            const value = Number((e.target as HTMLInputElement).value);
                            onChange(value);
                        }}
                    >
                        <Grid display={"flex"} justifyContent={"center"} size={4}>
                            <DotLegalRadioButton value={1} smallSideMargin></DotLegalRadioButton>
                        </Grid>
                        <Grid display={"flex"} justifyContent={"center"} size={4}>
                            <DotLegalRadioButton value={2} smallSideMargin></DotLegalRadioButton>
                        </Grid>
                        <Grid display={"flex"} justifyContent={"center"} size={4}>
                            <DotLegalRadioButton value={3} smallSideMargin></DotLegalRadioButton>
                        </Grid>
                    </RadioGroup>
                </Grid>
            </React.Fragment>
        );
    }

    function getContentHeader(label: string, removeTopMargin?: boolean) {
        return (
            <DotLegalHeader headerStyle={"medium"} marginTop={removeTopMargin ? 0 : 4}>
                {translateString(label)}
            </DotLegalHeader>
        );
    }

    return (
        <Box>
            {isLoading ? (
                <Box height={"70vh"}>
                    <DotLegalFullScreenSpinner />
                </Box>
            ) : (
                <Box sx={{ paddingLeft: 1, paddingRight: 1 }}>
                    {props.isPDF && <ProcessingActivityReportDialogHeader reportType={CustomerReportType.ResearchEthicsApproval} isPdf />}

                    <Box sx={{ marginTop: props.isPDF ? 2 : 0 }}>
                        {getApplicantContent()}

                        {getProjectInformationContent()}

                        {getCheckListContent()}

                        {getProjectDescriptionContent()}

                        {getParticipantsAndConfidentiallyContent()}

                        {getFinancialIncentivesContent()}

                        {getEnvironmentAndSafetyContent()}

                        {getOtherConditionsContent()}

                        {getSignatureContent()}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default ResearchEthicsApprovalReport;
